// MessageDetailsModal.jsx
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Chip,
  Divider,
} from "@mui/material";
import { X, MessageSquare, Smartphone, Calendar, User, Hash, Database, Zap } from "lucide-react";

const MessageDetailsModal = ({ open, onClose, message }) => {
  if (!message) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: "rounded-7xl shadow-2xl ",
      }}
    >
      {/* Header with gradient */}
      <DialogTitle className="p-0">
        <div className="bg-gradient-to-r from-red-500 to-red-600  p-8 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <MessageSquare className="w-6 h-6" />
              </div>
              <div>
                <Typography variant="h5" className="font-bold text-white">
                  Message Details
                </Typography>
                <Typography variant="body2" className="text-red-100">
                  Complete message information
                </Typography>
              </div>
            </div>
            <IconButton 
              onClick={onClose} 
              size="small"
              className="bg-white/20 hover:bg-white/30 text-white backdrop-blur-sm"
            >
              <X className="w-5 h-5" />
            </IconButton>
          </div>
        </div>
      </DialogTitle>

      <DialogContent className="p-0">
        <div className="space-y-8 p-8">
          {/* Message Preview Card */}
          <Box className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center space-x-2 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-red-600" />
              </div>
              <Typography variant="h6" className="font-bold text-gray-800">
                Message Content
              </Typography>
            </div>
            <Box className="bg-white p-5 rounded-xl border border-gray-200 shadow-inner">
              <Typography className="text-gray-800 leading-relaxed text-lg">
                {message.sms?.snd_txt || "No message content"}
              </Typography>
            </Box>
            <div className="mt-4 flex items-center text-sm text-gray-500">
              <div className="flex items-center space-x-4">
                <span className="flex items-center">
                  <Smartphone className="w-4 h-4 mr-1" />
                  {message.sms?.snd_to || "N/A"}
                </span>
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {message.date}
                </span>
              </div>
            </div>
          </Box>

          {/* Details Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Message Info */}
            <Box className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <Typography variant="h6" className="font-bold text-gray-800 mb-6 flex items-center">
                <div className="p-2 bg-blue-50 rounded-lg mr-3">
                  <Hash className="w-5 h-5 text-blue-600" />
                </div>
                Message Information
              </Typography>
              
              <div className="space-y-5">
                <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <Hash className="w-4 h-4 text-gray-600" />
                    </div>
                    <div>
                      <Typography variant="caption" className="text-gray-500 font-medium">
                        Message ID
                      </Typography>
                      <Typography className="font-bold text-gray-900">
                        {message.id}
                      </Typography>
                    </div>
                  </div>
                  <Chip
                    label="Unique ID"
                    size="small"
                    className="bg-gray-100 text-gray-700 font-medium"
                  />
                </div>

                <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-50 rounded-lg">
                      <User className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <Typography variant="caption" className="text-gray-500 font-medium">
                        Sender ID
                      </Typography>
                      <Typography className="font-bold text-gray-900">
                        {message.sender_id}
                      </Typography>
                    </div>
                  </div>
                  <Chip
                    label="Sender"
                    size="small"
                    className="bg-purple-100 text-purple-700 font-medium"
                  />
                </div>

                {/* <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-50 rounded-lg">
                      <Database className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <Typography variant="caption" className="text-gray-500 font-medium">
                        SMSC Provider
                      </Typography>
                      <Typography className="font-bold text-gray-900">
                        {message.sms?.snd_smsc || "N/A"}
                      </Typography>
                    </div>
                  </div>
                  <Chip
                    label="Provider"
                    size="small"
                    className="bg-green-100 text-green-700 font-medium"
                  />
                </div> */}
              </div>
            </Box>

            {/* Right Column - Status & Metrics */}
            <Box className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <Typography variant="h6" className="font-bold text-gray-800 mb-6 flex items-center">
                <div className="p-2 bg-red-50 rounded-lg mr-3">
                  <Zap className="w-5 h-5 text-red-600" />
                </div>
                Status & Metrics
              </Typography>
              
              <div className="space-y-5">
                <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${
                      message.sms_type === "bulk" ? "bg-blue-50" :
                      message.sms_type === "quick" ? "bg-orange-50" :
                      message.sms_type === "voice" ? "bg-purple-50" : "bg-gray-50"
                    }`}>
                      <MessageSquare className={`w-4 h-4 ${
                        message.sms_type === "bulk" ? "text-blue-600" :
                        message.sms_type === "quick" ? "text-orange-600" :
                        message.sms_type === "voice" ? "text-purple-600" : "text-gray-600"
                      }`} />
                    </div>
                    <div>
                      <Typography variant="caption" className="text-gray-500 font-medium">
                        Message Type
                      </Typography>
                      <Typography className="font-bold text-gray-900 capitalize">
                        {message.sms_type}
                      </Typography>
                    </div>
                  </div>
                  <Chip
                    label={message.sms_type}
                    className={`capitalize font-bold px-3 py-1 rounded-full text-sm
                      ${
                        message.sms_type === "bulk"
                          ? "bg-blue-100 text-blue-700"
                          : message.sms_type === "quick"
                          ? "bg-orange-100 text-orange-700"
                          : message.sms_type === "voice"
                          ? "bg-purple-100 text-purple-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    size="small"
                  />
                </div>

                <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${
                      message.status === "Completed" ? "bg-green-50" :
                      message.status === "Failed" ? "bg-red-50" : "bg-yellow-50"
                    }`}>
                      <div className={`w-4 h-4 rounded-full ${
                        message.status === "Completed" ? "bg-green-500" :
                        message.status === "Failed" ? "bg-red-500" : "bg-yellow-500"
                      }`} />
                    </div>
                    <div>
                      <Typography variant="caption" className="text-gray-500 font-medium">
                        Delivery Status
                      </Typography>
                      <Typography className="font-bold text-gray-900">
                        {message.status}
                      </Typography>
                    </div>
                  </div>
                  <Chip
                    label={message.status}
                    className={`font-bold px-3 py-1 rounded-full text-sm
                      ${
                        message.status === "Completed"
                          ? "bg-green-100 text-green-700"
                          : message.status === "Failed"
                          ? "bg-red-100 text-red-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    size="small"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-amber-50 rounded-lg">
                      <Zap className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <Typography variant="caption" className="text-gray-500 font-medium">
                        Units Consumed
                      </Typography>
                      <Typography className="font-bold text-gray-900">
                        {message.sms?.required_units || 0} units
                      </Typography>
                    </div>
                  </div>
                  <div className="text-right">
                    <Typography variant="caption" className="text-gray-500 font-medium block">
                      Total Sent
                    </Typography>
                    {/* <Typography className="font-bold text-gray-900">
                      {message.total_sent} message{message.total_sent !== 1 ? 's' : ''}
                    </Typography> */}
                  </div>
                </div>
              </div>
            </Box>
          </div>

          {/* Additional Stats */}
          {/* <Box className="bg-gradient-to-r from-red-50 to-red-100/50 p-6 rounded-2xl border border-red-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                <Typography variant="caption" className="text-gray-500 font-medium">
                  Encoder
                </Typography>
                <Typography className="font-bold text-gray-900 text-lg">
                  {message.sms?.msg_encoder || "0"}
                </Typography>
              </div>
              <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                <Typography variant="caption" className="text-gray-500 font-medium">
                  Characters
                </Typography>
                <Typography className="font-bold text-gray-900 text-lg">
                  {message.sms?.snd_txt?.length || 0}
                </Typography>
              </div>
              <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                <Typography variant="caption" className="text-gray-500 font-medium">
                  Group Slug
                </Typography>
                <Typography className="font-bold text-gray-900 text-lg truncate">
                  {message.sms?.contact_group_slugs ? "✓" : "None"}
                </Typography>
              </div>
              <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                <Typography variant="caption" className="text-gray-500 font-medium">
                  Message Parts
                </Typography>
                <Typography className="font-bold text-gray-900 text-lg">
                  {message.sms?.required_units || 1}
                </Typography>
              </div>
            </div>
          </Box> */}
        </div>
      </DialogContent>

      <DialogActions className="p-6 border-t border-gray-100">
        <div className="flex w-full justify-between items-center">
          <div className="flex items-center space-x-2 text-gray-500">
            <MessageSquare className="w-4 h-4" />
            <Typography variant="caption" className="font-medium">
              SMS Details • {new Date().toLocaleDateString()}
            </Typography>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-100 text-gray-800 rounded-xl hover:bg-gray-200 transition-all duration-200 font-medium shadow-sm hover:shadow"
            >
              Close
            </button>
            {/* <button
              onClick={() => {
                //console.log("Export message:", message);
                onClose();
              }}
              className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Export Details
            </button> */}
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default MessageDetailsModal;